import { barcelonaMidnights } from '../../domain/dateTime'
import { type EventMarker, eventMarkers } from './summarise'
import type { EventRow } from './types'

/** One event's bar: where it starts and ends along the chart, as fractions of its width (0–1). */
export interface GanttBar {
  marker: EventMarker
  from: number
  to: number
  /** No end yet: the bar runs on to `now` (or the chart's end) and fades out. */
  open: boolean
}

export interface GanttChart {
  /** The chart's span, in ms: the earliest start to the latest start or end (or `now`, see `ganttChart`). */
  start: number
  end: number
  /** One per event, in the list's Eventos order (see `eventMarkers`). */
  bars: GanttBar[]
  /**
   * The chart's rows, top to bottom: a bar each, but every MARPOL shares one
   * row, placed where the first MARPOL comes.
   */
  rows: GanttBar[][]
  /** Barcelona midnights within the span: their time and place (0–1). */
  days: { at: number; position: number }[]
  /** Where `now` falls (0–1); null when outside the span. */
  now: number | null
}

/** At most this many day lines, so a long scale keeps its labels readable. */
const MAX_DAYS = 12
const HOUR = 3_600_000

/**
 * A scale's events as a Gantt chart: a bar per event on one shared time axis,
 * so overlapping MARPOL/BOMBEO events and stays show at a glance. The axis
 * runs from the earliest start to the latest start or end, and on to `now`
 * while the vessel is still at a berth (a stay without end that has started).
 * Null without events.
 */
export const ganttChart = (events: readonly EventRow[], now: number): GanttChart | null => {
  const markers = eventMarkers(events, now)
  if (markers.length === 0) return null
  const times = markers.flatMap(m => [Date.parse(m.startTime), ...(m.endTime === null ? [] : [Date.parse(m.endTime)])])
  const berthed = markers.some(m => m.type === 'PLACE' && m.endTime === null && Date.parse(m.startTime) <= now)
  const start = Math.min(...times)
  const latest = Math.max(...times, ...(berthed ? [now] : []))
  // Never a zero-width span: at least an hour.
  const end = Math.max(latest, start + HOUR)
  const position = (at: number): number => Math.min(1, Math.max(0, (at - start) / (end - start)))

  const bars = markers.map(marker => {
    const from = Date.parse(marker.startTime)
    const to = marker.endTime === null ? (now > from ? Math.min(now, end) : end) : Date.parse(marker.endTime)
    return { marker, from: position(from), to: position(to), open: marker.endTime === null }
  })
  const rows: GanttBar[][] = []
  let marpols: GanttBar[] | undefined
  for (const bar of bars) {
    if (bar.marker.type !== 'MARPOL') rows.push([bar])
    else if (marpols) marpols.push(bar)
    else {
      marpols = [bar]
      rows.push(marpols)
    }
  }
  const midnights = barcelonaMidnights(start, end)
  const step = Math.max(1, Math.ceil(midnights.length / MAX_DAYS))
  const days = midnights.filter((_, index) => index % step === 0).map(at => ({ at, position: position(at) }))
  return { start, end, bars, rows, days, now: now >= start && now <= end ? position(now) : null }
}
